import SidebarBackground from '@assets/images/sidebar_bg.png';
import UpercutLetter from '@assets/svgs/upercut-letter.svg';
import UpercutLogo from '@assets/svgs/upercut-white.svg';
import UpercutLetterWhiteMode from '@assets/svgs/white-mode/upercut-letter.svg';
import UpercutLogoWhiteMode from '@assets/svgs/white-mode/upercut-logo.svg';
import { filterDashboardMenuItemsByPermission, getDashboardMenuItems, DashboardMenuItem } from '@constants/DashboardMenu';
import { useAuth } from '@contexts/AuthContext';
import { usePermissionContext } from '@contexts/PermissionContext';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Box, IconButton, ListItemButton, ListItemIcon, Tooltip, Typography, useTheme } from '@mui/material';
import MuiDrawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import { styled, CSSObject, Theme } from '@mui/material/styles';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { useSidebarV2 } from './SidebarV2Context';
import { SIDEBAR_FULL_WIDTH, SIDEBAR_ICON_WIDTH } from './constants';

const openedMixin = (theme: Theme): CSSObject => ({
    width: SIDEBAR_FULL_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.enteringScreen,
    }),
    overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
    width: SIDEBAR_ICON_WIDTH,
    transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
    }),
    overflowX: 'hidden',
});

const Drawer = styled(MuiDrawer, {
    shouldForwardProp: prop => prop !== 'open',
})(({ theme, open }) => ({
    width: open ? SIDEBAR_FULL_WIDTH : SIDEBAR_ICON_WIDTH,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': closedMixin(theme),
    }),
}));

/**
 * Flatten menu items for V2 sidebar: top-level items only, no nested subItems.
 * Items with subItems but isLink=false get their first subItem's path as the link.
 */
const flattenMenuItems = (items: DashboardMenuItem[]): DashboardMenuItem[] => {
    return items.map(item => ({
        ...item,
        isLink: true,
        path: item.isLink ? item.path : (item.subItems?.[0]?.path ?? item.path),
        subItems: undefined,
    }));
};

export const SidebarV2 = () => {
    const { mode, toggleMode } = useSidebarV2();
    const { user } = useAuth();
    const { hasPermission, loading } = usePermissionContext();
    const location = useLocation();
    const currentPath = location.pathname;
    const theme = useTheme();
    const isOpen = mode === 'full';

    const menuItems = useMemo(() => {
        if (loading) return [];
        const filtered = filterDashboardMenuItemsByPermission(getDashboardMenuItems(user?.role), hasPermission);
        return flattenMenuItems(filtered);
    }, [hasPermission, loading, user?.role]);

    return (
        <Box sx={{ display: 'flex' }}>
            <Drawer
                variant="permanent"
                open={isOpen}
                sx={{
                    '& .MuiDrawer-paper': {
                        backgroundImage: theme.palette.mode === 'dark' ? `url(${SidebarBackground})` : 'none',
                        backgroundSize: 'cover',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'center',
                        zIndex: 1,
                        backgroundColor: theme.palette.mode === 'dark' ? 'transparent' : '#ffffff',
                        display: 'flex',
                        flexDirection: 'column',
                        borderRight: `1px solid ${theme.palette.mode === 'dark' ? 'rgb(61, 61, 61)' : theme.palette.border.main}`,
                    },
                }}
            >
                {/* Header */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: isOpen ? 'row' : 'column',
                        justifyContent: isOpen ? 'space-between' : 'center',
                        alignItems: 'center',
                        padding: '16px 12px',
                        minHeight: '64px',
                    }}
                >
                    {!isOpen && (
                        <IconButton
                            onClick={toggleMode}
                            size="small"
                            sx={{
                                color: theme.palette.lightGrey.main,
                                mb: '4px',
                                '&:hover': {
                                    background: theme.palette.backgroundPilsurface.main,
                                },
                            }}
                        >
                            <ChevronRightIcon fontSize="small" />
                        </IconButton>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <img
                            src={theme.palette.mode === 'dark' ? UpercutLogo : UpercutLogoWhiteMode}
                            alt="Upercut Logo"
                            style={{ maxHeight: '31px', marginRight: isOpen ? '12px' : '0px' }}
                        />
                        {isOpen && (
                            <img
                                src={theme.palette.mode === 'dark' ? UpercutLetter : UpercutLetterWhiteMode}
                                alt="Upercut"
                                style={{ maxHeight: '31px' }}
                            />
                        )}
                    </Box>
                    {isOpen && (
                        <IconButton
                            onClick={toggleMode}
                            size="small"
                            sx={{
                                color: theme.palette.lightGrey.main,
                                '&:hover': {
                                    background: theme.palette.backgroundPilsurface.main,
                                },
                            }}
                        >
                            <ChevronLeftIcon fontSize="small" />
                        </IconButton>
                    )}
                </Box>

                {/* Menu items */}
                <Box sx={{ flex: 1 }}>
                    <List sx={{ px: '8px', py: 0, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        {menuItems.map(item => {
                            const isActive = currentPath === item.path || (item.path !== '/' && currentPath.startsWith(item.path));

                            const button = (
                                <ListItemButton
                                    key={item.id}
                                    component={Link}
                                    to={item.path}
                                    selected={isActive}
                                    sx={{
                                        '&.Mui-selected': {
                                            background: theme.palette.primary.main,
                                            color: theme.palette.secondary.main,
                                            '&:hover': {
                                                background: theme.palette.primary.main,
                                            },
                                        },
                                        color: theme.palette.lightGrey.main,
                                        borderRadius: '12px',
                                        display: 'flex',
                                        justifyContent: isOpen ? 'flex-start' : 'center',
                                        alignItems: 'center',
                                        gap: '12px',
                                        padding: isOpen ? '12px' : '12px 0',
                                        minHeight: '44px',
                                        '&:hover': {
                                            background: theme.palette.backgroundPilsurface.main,
                                            color: isActive && theme.palette.mode === 'light'
                                                ? theme.palette.lightGrey.main
                                                : theme.palette.secondary.main,
                                        },
                                    }}
                                >
                                    <ListItemIcon sx={{ minWidth: 0, justifyContent: 'center' }}>
                                        {
                                            typeof item.icon === 'string' ? (
                                                <i
                                                    className={item.icon}
                                                    style={{
                                                        height: '20px',
                                                        width: '20px',
                                                        color: isActive ? theme.palette.secondary.main : theme.palette.lightGrey.main,
                                                    }}
                                                />
                                            ) : (
                                                <Box
                                                    sx={{
                                                        height: '20px',
                                                        width: '20px',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        color: isActive ? theme.palette.secondary.main : theme.palette.lightGrey.main,
                                                    }}
                                                >
                                                    {item.icon}
                                                </Box>
                                            )
                                        }
                                    </ListItemIcon>
                                    {isOpen && (
                                        <Typography variant="labelMediumBold" noWrap>
                                            {item.title}
                                        </Typography>
                                    )}
                                </ListItemButton>
                            );

                            if (!isOpen) {
                                return (
                                    <Tooltip key={item.id} title={item.title} placement="right" arrow>
                                        {button}
                                    </Tooltip>
                                );
                            }

                            return button;
                        })}
                    </List>
                </Box>

            </Drawer>
        </Box>
    );
};
